import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { SiteFooter } from './SiteFooter';
import { trackConversion } from '@/lib/analytics';
import {
  ArrowLeft,
  ArrowRight,
  User,
  Mail,
  Globe,
  MessageSquare,
  ChevronDown,
  Loader2,
  CheckCircle,
} from 'lucide-react';

const SUBJECTS = [
  'Consulting / Strategy',
  'Workshops for Teams',
  'Collaboration / Partnership',
  'Press / Podcast',
  'Question about the Deck',
  'Something Else',
];

const TIMELINES = [
  'ASAP',
  'Within 2–4 weeks',
  'Just exploring',
];

export const ContactPage = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [timeline, setTimeline] = useState('');
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = name.trim() && email.trim() && email.includes('@') && subject && message.trim() && consent;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject,
          message: message.trim(),
          website: website.trim(),
          timeline,
        }),
      });
      if (!res.ok) throw new Error('Failed to send');
      setIsSubmitted(true);
      trackConversion('contact_lead');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = cn(
    "w-full p-3 rounded-xl border-2 border-[#d4c4a8]/20",
    "bg-[#0a1628]/50 text-[#e8dcc8] placeholder-[#d4c4a8]/30",
    "font-body text-sm",
    "focus:outline-none focus:border-[#e89b47]/50 focus:ring-1 focus:ring-[#e89b47]/30",
    "transition-all duration-300"
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628] text-[#e8dcc8]">
      {/* Top bar */}
      <div className="max-w-6xl mx-auto px-6 pt-6 flex items-center justify-between gap-3">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-[#d4c4a8]/60 hover:text-[#e89b47] transition-colors font-body text-sm"
          data-testid="contact-back-btn"
        >
          Back to Home
        </button>
        <a
          href="https://calendly.com/marketingtarot/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 h-10 px-5 text-sm bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] hover:from-[#e89b47]/90 hover:to-[#d4883a]/90 font-display tracking-widest shadow-lg shadow-[#e89b47]/25 rounded-xl transition-all hover:scale-105 hover:shadow-xl hover:shadow-[#e89b47]/30"
          data-testid="contact-book-call-btn"
        >
          Book a Clarity Call
        </a>
      </div>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 pt-16 pb-10 text-center">
        <p className="font-display text-sm tracking-[0.3em] text-[#e89b47] mb-6 uppercase">
          Contact Us
        </p>
        <h1 className="font-display text-3xl sm:text-4xl text-[#e8dcc8] leading-tight mb-4" data-testid="contact-headline">
          Let's Connect
        </h1>
        <p className="font-body text-[#d4c4a8]/70 max-w-xl mx-auto">
          Tell us what you're looking for, and we'll guide you toward the clarity you need.
        </p>
      </section>

      {/* Form or Success */}
      <section className="max-w-2xl mx-auto px-6 pb-20">
        {isSubmitted ? (
          <div className={cn(
            "p-10 rounded-2xl text-center",
            "bg-[#1e2a4a]/50 border border-green-500/30"
          )} data-testid="contact-success">
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h2 className="font-display text-2xl text-[#e8dcc8] mb-3">
              Thank you — your message has been received.
            </h2>
            <p className="font-body text-[#d4c4a8]/70 mb-8">
              We'll get back to you within 1–2 business days.
            </p>
            {(subject === 'Consulting / Strategy' || subject === 'Workshops for Teams') ? (
              <a
                href="https://calendly.com/marketingtarot/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-12 px-8 text-sm bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] font-display tracking-widest rounded-xl shadow-lg shadow-[#e89b47]/25 transition-all hover:scale-105 hover:shadow-xl hover:shadow-[#e89b47]/30"
                data-testid="book-clarity-call-cta"
              >
                Book a Clarity Call
                <ArrowRight className="w-4 h-4" />
              </a>
            ) : (
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 h-12 px-8 text-sm bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] font-display tracking-widest rounded-xl shadow-lg shadow-[#e89b47]/25 transition-all hover:scale-105 hover:shadow-xl hover:shadow-[#e89b47]/30"
                data-testid="explore-card-btn"
              >
                While you wait, explore a card
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            {/* Name */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
                <User className="w-4 h-4 text-[#e89b47]" />
                Name <span className="text-[#e89b47]">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full Name"
                className={inputClass}
                data-testid="contact-name"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
                <Mail className="w-4 h-4 text-[#e89b47]" />
                Email Address <span className="text-[#e89b47]">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={inputClass}
                data-testid="contact-email"
              />
            </div>

            {/* Subject dropdown */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
                <MessageSquare className="w-4 h-4 text-[#e89b47]" />
                What are you reaching out about? <span className="text-[#e89b47]">*</span>
              </label>
              <div className="relative">
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className={cn(inputClass, "appearance-none cursor-pointer", !subject && "text-[#d4c4a8]/30")}
                  data-testid="contact-subject"
                >
                  <option value="" disabled>Select a topic</option>
                  {SUBJECTS.map((s) => (
                    <option key={s} value={s} className="bg-[#1e2a4a] text-[#e8dcc8]">{s}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#d4c4a8]/40 pointer-events-none" />
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
                Tell us a little more <span className="text-[#e89b47]">*</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Share a few details so we can understand what you need."
                rows={5}
                className={cn(inputClass, "resize-none")}
                data-testid="contact-message"
              />
            </div>

            {/* Website */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 font-display text-[#e8dcc8] text-sm">
                <Globe className="w-4 h-4 text-[#e89b47]" />
                Your Website / Social Link
                <span className="text-[#d4c4a8]/40 font-body text-xs ml-1">(optional)</span>
              </label>
              <input
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://"
                className={inputClass}
                data-testid="contact-website"
              />
            </div>

            {/* Timeline */}
            <div className="space-y-3">
              <label className="font-display text-[#e8dcc8] text-sm">
                How soon do you want to get started?
              </label>
              <div className="flex flex-wrap gap-3">
                {TIMELINES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTimeline(t)}
                    className={cn(
                      "px-4 py-2 rounded-xl border-2 font-body text-sm transition-all",
                      timeline === t
                        ? "border-[#e89b47] bg-[#e89b47]/10 text-[#e89b47]"
                        : "border-[#d4c4a8]/20 text-[#d4c4a8]/60 hover:border-[#d4c4a8]/40"
                    )}
                    data-testid={`timeline-${t.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Consent */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 accent-[#e89b47] w-4 h-4 cursor-pointer"
                data-testid="contact-consent"
              />
              <label className="font-body text-sm text-[#d4c4a8]/70 cursor-pointer" onClick={() => setConsent(!consent)}>
                I agree to be contacted by The Marketing Tarot regarding my inquiry.
              </label>
            </div>

            {/* Error */}
            {error && (
              <p className="text-red-400 text-sm text-center" data-testid="contact-error">{error}</p>
            )}

            {/* Submit */}
            <Button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className={cn(
                "w-full h-14 text-base font-display tracking-widest rounded-xl transition-all",
                "bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628]",
                "hover:from-[#e89b47]/90 hover:to-[#d4883a]/90",
                "shadow-lg shadow-[#e89b47]/25",
                "disabled:opacity-40 disabled:cursor-not-allowed"
              )}
              data-testid="contact-submit"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Send Your Message
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
            <p className="font-body text-[#d4c4a8]/40 text-xs text-center">
              We usually respond within 1–2 business days.
            </p>
          </form>
        )}
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
};

export default ContactPage;
